<template>
  <div>
    <NavigationBreadcrumb :items="breadcrumb_items"></NavigationBreadcrumb>
    <h3 class="mt-3">
      {{ $getVisibleNames("mesh.competence", true, "Competencias") }}
      <AlternativeNameButton
        :table_name="'mesh.competence'"
        :allows_crud="allows_cruds"
      ></AlternativeNameButton>
    </h3>
    <div style="display: flex">
      <div
        class="col"
        style="text-align: left"
        v-if="allows_cruds && user && user.is_superuser"
      >
        <ButtonMedium
          v-can="'mesh.add_competence'"
          v-if="allows_cruds && user && user.is_superuser"
          :tooltip_text="
            $t('reusable.common.addTooltip', {
              entity: $getVisibleNames('mesh.competence', false, 'Competencia'),
            })
          "
          :text_button="$t('reusable.common.add')"
          v-b-modal.new-competence-modal
          :icon="'plus'"
          :variant="'primary'"
        >
        </ButtonMedium>
      </div>
    </div>

    <NewGenericBTable
      :key="keyGenericTable"
      :fields="fields"
      :pre_current_page="current_page"
      :pre_input_search="saved_input_search"
      :pre_multiple_search="saved_multiple_search"
      @current_page="slotUpdateCurrentPage"
      @actual_items="slotActualItems"
      @input_search_changed="slotInputSearchChanged"
      @current_tag="slotMultipleSearchChanged"
      :url="'/mesh/alt-competence/'"
      :show_pagination="true"
      :search_filter="true"
      :columns_display="true"
      :display_id="display_id"
    >
      <template #cell(full_sentence)="row">
        <div class="d-flex">
          <SentenceContainer :Sentence="row.item">
            <template #order>
              <span class="mr-1">
                <span
                  v-if="
                    row.item.profile_cycle_order &&
                    row.item.profile_cycle_order != 9999
                  "
                  >{{ row.item.profile_cycle_order }}.</span
                >
                <span v-if="row.item.order">{{ row.item.order }}</span>
                <span
                  v-if="
                    row.item.sub_order > 0 &&
                    institution &&
                    institution.show_competence_sub_order
                  "
                  >.{{ row.item.sub_order }}</span
                >
              </span>
            </template>
          </SentenceContainer>
        </div>
      </template>

      <template #cell(type)="row">
        <template v-if="row.item.type && competenceType(row.item.type)">
          <template v-for="type in [competenceType(row.item.type)]">
            <span
              v-if="type"
              v-b-tooltip.v-secondary="$htmlToPlainText(type.description)"
              :key="type.id"
              >{{ type.name }}</span
            >
          </template>
        </template>
      </template>

      <template #cell(egress_profile)="row">
        <div class="profiles-container">
          <template v-for="profiles in getEgressProfileCompetence(row.item.id)">
            <div class="profiles-content" :key="profiles.id">
              {{ profiles.professional_title }}
              <p
                v-if="
                  !(institution && institution.internal_use_id == 'duoc.cl')
                "
              >
                <strong
                  >{{
                    $getVisibleNames("mesh.cycle", false, "Ciclo Formativo")
                  }}: </strong
                >{{
                  profiles.cycle_name == ""
                    ? $t("reusable.common.notApplicable")
                    : profiles.cycle_name
                }}
              </p>
              <p v-if="profiles.mentions.length > 0">
                <strong
                  >{{
                    $getVisibleNames(
                      "mesh.egressprofilecompetencemention",
                      profiles.mentions.length > 1,
                      "Mención"
                    )
                  }}: </strong
                >{{
                  profiles.mentions.length > 1
                    ? "(" + profiles.mentions.join(", ") + ")"
                    : profiles.mentions[0]
                }}
              </p>
            </div>
          </template>
        </div>
        <div class="container-badge-profile d-flex">
          <h5>
            <b-badge
              class="badge-profile"
              variant="secondary"
              :id="`badge-profile-${row.item.id}`"
            >
              {{ getEgressProfileCompetence(row.item.id).length }}
            </b-badge>
          </h5>
        </div>

        <b-popover
          :target="`badge-profile-${row.item.id}`"
          triggers="hover"
          placement="left"
          v-if="
            tableReady && getEgressProfileCompetence(row.item.id).length > 0
          "
        >
          <div class="badge-popover">
            <template
              v-for="profiles in getEgressProfileCompetence(row.item.id)"
            >
              <div class="profiles-content" :key="profiles.id">
                {{ profiles.professional_title }}
                <p
                  v-if="
                    !(institution && institution.internal_use_id == 'duoc.cl')
                  "
                >
                  <strong
                    >{{
                      $getVisibleNames("mesh.cycle", false, "Ciclo Formativo")
                    }}: </strong
                  >{{
                    profiles.cycle_name == ""
                      ? $t("reusable.common.notApplicable")
                      : profiles.cycle_name
                  }}
                </p>
                <p v-if="profiles.mentions.length > 0">
                  <strong
                    >{{
                      $getVisibleNames(
                        "mesh.egressprofilecompetencemention",
                        profiles.mentions.length > 1,
                        "Mención"
                      )
                    }}: </strong
                  >{{
                    profiles.mentions.length > 1
                      ? "(" + profiles.mentions.join(", ") + ")"
                      : profiles.mentions[0]
                  }}
                </p>
              </div>
            </template>
          </div>
        </b-popover>
      </template>

      <template #cell(ra_base)="row">
        <h5
          :key="`ra-base-header-${row.item.id}`"
          :id="`ra-base-header-${row.item.id}`"
          v-bind:class="{ 'pointer-class': allows_cruds }"
          @click="
            () => {
              if (allows_cruds) $bvModal.show(`modal-base-ra-${row.item.id}`);
            }
          "
        >
          <b-badge class="badge" v-if="raBasesFiltered" variant="secondary">{{
            raBasesFiltered(row.item.id).length
          }}</b-badge>
        </h5>
        <b-modal
          :id="`modal-base-ra-${row.item.id}`"
          hide-footer
          :title="$getVisibleNames('teaching.basera', false, 'RA Base')"
          size="lg"
        >
          <RaBase :competence_id="row.item.id"></RaBase>
        </b-modal>
        <b-popover
          :target="`ra-base-header-${row.item.id}`"
          :key="`ra-base-tooltip-${row.item.id}`"
          triggers="hover"
          v-if="tableReady"
        >
          <template #title>{{
            $getVisibleNames("teaching.basera", false, "RA Base")
          }}</template>
          <template v-if="raBasesFiltered(row.item.id).length > 0">
            <div class="ra-competences-container">
              <span v-for="ra in raBasesFiltered(row.item.id)" :key="ra.id">
                <SentenceContainer :Sentence="ra"> </SentenceContainer>
              </span>
            </div>
          </template>
          <template v-else>
            {{
              $t("reusable.common.noCreated", {
                entity: $getVisibleNames("teaching.basera", false, "RA Base"),
              })
            }}
          </template>
        </b-popover>
      </template>

      <template v-if="allows_cruds" #cell(actions)="row">
        <ButtonSmall
          v-can="'mesh.change_competence'"
          :tooltip_text="
            $t('reusable.common.editTooltip', {
              entity: $getVisibleNames('mesh.competence', false, 'Competencia'),
            })
          "
          :click_button="
            () => $bvModal.show(`edit-competence-modal-${row.item.id}`)
          "
          :class="``"
          :icon="'square'"
        ></ButtonSmall>
        <ButtonSmall
          v-can="'mesh.delete_competence'"
          v-if="allows_cruds && user && user.is_superuser"
          :click_button="() => askForDeleteCompetence(row.item.id)"
          :tooltip_text="
            $t('reusable.common.deleteTooltip', {
              entity: $getVisibleNames('mesh.competence', false, 'Competencia'),
            })
          "
          :icon="'trash'"
        ></ButtonSmall>
        <b-modal
          :id="`edit-competence-modal-${row.item.id}`"
          :title="
            $t('reusable.common.editModalTitle', {
              entity: $getVisibleNames('mesh.competence', false, 'Competencia'),
            })
          "
          size="xl"
          hide-footer
          @hide="resetCompetenceTable"
        >
          <CompetenceForm
            :Competence="row.item"
            :allows_crud="allows_cruds"
            :can_update="allows_cruds"
            :can_delete="allows_cruds"
            @updated="slotUpdatedCompetence"
            @close="slotCloseModal"
            :selected_ids="getEgressProfiles(row.item.id)"
            :show_title="false"
            @reset_competence="changeCompetenceDescriptor(row.item.id)"
            @change_middle_descriptors="
              () => {
                changeCompetenceDescriptor(row.item.id);
              }
            "
            @reset_competence_table="countCompetenceTable"
          ></CompetenceForm>
        </b-modal>
      </template>

      <template
        v-for="descriptor in competenceDescriptor"
        v-slot:[`cell(${descriptor.key})`]="row"
      >
        <RenderVHtml
          :key="descriptor.key"
          :content="row.item[descriptor.key]"
        ></RenderVHtml>
      </template>
    </NewGenericBTable>

    <b-modal
      id="new-competence-modal"
      :title="
        $t('reusable.common.createModalTitle', {
          entity: $getVisibleNames('mesh.competence', false, 'Competencia'),
        })
      "
      size="xl"
      hide-footer
    >
      <!-- @hide="resetCompetenceTable" -->
      <CompetenceForm
        @created="slotCreatedCompetence"
        @close="slotCloseModal"
        :show_title="false"
        :allows_crud="allows_cruds"
        :can_update="allows_cruds"
        :can_delete="allows_cruds"
      ></CompetenceForm>
    </b-modal>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "CompetenceView",
  components: {
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
    CompetenceForm: () => import("./CompetenceForm"),
    SentenceContainer: () => import("@/components/reusable/SentenceContainer"),
    NewGenericBTable: () =>
      import("@/components/reusable/NewGenericBTable.vue"),
    RaBase: () => import("@/components/reusable/RaBase"),
    AlternativeNameButton: () =>
      import("@/components/reusable/AlternativeNameButton"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
  },
  props: {
    display_id: {},
  },
  data() {
    return {
      keyGenericTable: 0,
      countChangeDescriptors: 0,
      current_page: 1,
      saved_input_search: "",
      saved_multiple_search: [],
      actualCompetences: [],
      descriptorKeysLoaded: [],
      tableReady: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      institution: "getInstitution",
      cycles: names.CYCLES,
      egressProfiles: names.EGRESS_PROFILES,
      ra_bases: names.RA_BASES,
      competenceTypes: names.COMPETENCE_TYPES,
      profileCompetences: names.PROFILE_COMPETENCES,
      profile_competences_mention: names.PROFILE_COMPETENCES_MENTION,
      profile_cycles: names.PROFILE_CYCLES,
    }),
    breadcrumb_items() {
      return [
        {
          text: this.$t("reusable.breadcrumb.home"),
          to: "/home",
        },
        {
          text: this.$getVisibleNames("mesh.competence", true, "Competencias"),
        },
      ];
    },
    allows_cruds() {
      if (
        this.user &&
        (this.user.group_names.includes(
          "DIRECTIVOS ACAD�MICOS INSTITUCIONALES"
        ) ||
          this.user.is_superuser)
      ) {
        const has_permission = this.$hasPermissions("mesh.add_competence");
        if (has_permission) {
          this.$store.commit(names.MUTATE_VISIBILITY_CRUD, true);
          return has_permission;
        } else return false;
      } else {
        this.$store.commit(names.MUTATE_VISIBILITY_CRUD, false);
        return false;
      }
    },
    competenceDescriptor() {
      const competence_list = this.actualCompetences.filter(
        (x) => x.descriptions && x.descriptions.length > 0
      );
      let distinct = [];
      competence_list.forEach((competence) => {
        competence.descriptions.forEach((descriptor) => {
          if (
            distinct.filter(
              (x) => x.key == "descriptor_" + descriptor.descriptor__id
            ).length == 0
          )
            distinct.push({
              key: "descriptor_" + descriptor.descriptor__id,
              label: descriptor.descriptor__title,
              display_column: true,
              tdClass: "text-left",
            });
        });
      });
      return distinct;
    },
    fields() {
      return [
        {
          key: "full_sentence",
          label: this.$t("curricularProducts.competence.writingLabel"),
          sortable: true,
          display_column: true,
          tdClass: "text-left",
        },
        this.actualCompetences.some((x) => x.type != null)
          ? {
              key: "type",
              label: this.$getVisibleNames(
                "mesh.competencetype",
                false,
                "Tipos de Competencias"
              ),
              display_column: true,
              tdClass: "text-left",
            }
          : { key: "type", label: "", display_column: false },
        {
          key: "egress_profile",
          label: String(
            this.$getVisibleNames("mesh.career", true, "Programas")
          ),
          display_column: true,
          tdClass: "text-left",
        },
        this.institution && this.institution.show_base_ra
          ? {
              key: "ra_base",
              label: this.$getVisibleNames("teaching.basera", true, "RA Base"),
              display_column: true,
              tdClass: "text-center",
            }
          : { key: "ra_base", label: "", display_column: false },
      ]
        .concat(this.competenceDescriptor)
        .concat([
          {
            key: "actions",
            label: "",
            display_column: true,
            thClass: "noprint",
            tdClass: "noprint",
          },
        ]);
    },
  },
  methods: {
    slotUpdateCurrentPage(page) {
      this.current_page = page;
    },
    slotInputSearchChanged(value) {
      this.saved_input_search = value;
    },
    slotMultipleSearchChanged(value) {
      this.saved_multiple_search = value;
    },
    slotActualItems(items) {
      this.tableReady = false;
      const newKeys = new Set();
      for (const competence of items) {
        for (const desc of competence.descriptions || []) {
          const key = "descriptor_" + desc.descriptor__id;
          competence[key] = desc.description;
          if (!this.descriptorKeysLoaded.includes(key)) newKeys.add(key);
        }
      }
      this.actualCompetences = items;
      if (newKeys.size > 0) {
        this.descriptorKeysLoaded.push(...newKeys);
        this.$nextTick(() => {
          this.keyGenericTable++;
          this.$nextTick(() => {
            this.tableReady = true;
          });
        });
      } else {
        this.$nextTick(() => {
          this.tableReady = true;
        });
      }
      this.fetchDataForVisibleCompetences(items);
    },
    fetchDataForVisibleCompetences(items) {
      if (!items || items.length === 0) return;
      const competenceIds = items.map((x) => x.id);
      // Una sola petición para todos los RA Bases de las competencias visibles
      this.$store.dispatch(names.FETCH_RA_BASES, {
        competence_ids: competenceIds,
      });
      // Una sola petición para todos los profile_competences de las competencias visibles
      this.$store
        .dispatch(names.FETCH_PROFILE_COMPETENCES, {
          competence_ids: competenceIds,
        })
        .then(() => {
          const egressProfileIds = [
            ...new Set(
              this.profileCompetences
                .filter((x) => competenceIds.includes(x.competence))
                .map((x) => x.egress_profile)
            ),
          ];
          if (egressProfileIds.length === 0) return;
          // Una petición con todos los egress profiles necesarios
          this.$store.dispatch(names.FETCH_EGRESS_PROFILES, {
            egress_profile_ids: egressProfileIds,
          });
          // Una sola petición para profile_cycles y mentions de todos los egress profiles
          this.$store.dispatch(names.FETCH_PROFILE_CYCLES, {
            egress_profile_ids: egressProfileIds,
          });
          // this.$store.dispatch(names.FETCH_PROFILE_COMPETENCES_MENTION, {
          //   egress_profile_ids: egressProfileIds,
          // });
        });
    },
    raBasesFiltered(comptence_id) {
      if (!comptence_id) return [];
      return this.ra_bases
        .filter((x) => x.competences.includes(comptence_id))
        .sort((a, b) => a.order - b.order);
    },
    changeCompetenceDescriptor(id) {
      this.$store.dispatch(names.PATCH_COMPETENCE, {
        competence_id: id,
      });
    },
    getMentions(profile_competence) {
      if (!profile_competence) return "";
      let mentions_name = this.profile_competences_mention
        .filter(
          (x) =>
            x.egress_profile == profile_competence.egress_profile &&
            x.egress_profile_competences.includes(profile_competence.id)
        )
        .sort(function (a, b) {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        })
        .map((x) => x.name);
      return mentions_name;
    },
    getNameCycle(id) {
      if (!id) return "";
      let cycle = this.cycles.find((x) => x.id == id);
      return cycle ? cycle.name : "";
    },
    getEgressProfile(id) {
      if (!id) return "";
      let egress_profile = this.egressProfiles.find((x) => x.id == id);
      return egress_profile ? egress_profile.professional_title : "";
    },
    getEgressProfileCompetence(competence_id) {
      let list = [];
      if (competence_id != null) {
        const profile_competences = this.profileCompetences.filter(
          (x) => x.competence == competence_id
        );
        profile_competences.forEach((profile_competence) => {
          profile_competence.professional_title = this.getEgressProfile(
            profile_competence.egress_profile
          );
          profile_competence.cycle_name = this.getNameCycle(
            profile_competence.cycle
          );
          profile_competence.mentions = this.getMentions(profile_competence);
          list.push(profile_competence);
        });
      }
      return list;
    },
    getEgressProfiles(competence_id) {
      let list = [];
      if (competence_id != null) {
        const profile_competences = this.profileCompetences.filter(
          (x) => x.competence == competence_id
        );
        profile_competences.forEach((profile_competence) => {
          const egress_profile = this.egressProfiles.find(
            (x) => x.id == profile_competence.egress_profile
          );
          const index = list.findIndex(
            (x) => x.id == profile_competence.egress_profile
          );
          if (egress_profile && index == -1) list.push(egress_profile);
        });
      }
      return list;
    },
    resetCompetenceTable() {
      if (this.keyGenericTable != this.countChangeDescriptors) {
        this.keyGenericTable = this.countChangeDescriptors;
        this.countChangeDescriptors++;
      }
    },
    countCompetenceTable() {
      this.countChangeDescriptors += 1;
    },
    competenceType(type_id) {
      return this.competenceTypes.find((x) => x.id == type_id);
    },
    slotCreatedCompetence() {
      this.$bvModal.hide("new-competence-modal");
      this.tableReady = false;
      this.keyGenericTable++;
    },
    slotUpdatedCompetence(competence) {
      this.$bvModal.hide(`edit-competence-modal-${competence.id}`);
      this.tableReady = false;
      this.keyGenericTable++;
    },
    askForDeleteCompetence(competence_id) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle", {
          entity: this.$getVisibleNames(
            "mesh.competence",
            false,
            "Competencia"
          ),
        }),
        text: this.$t("reusable.common.deleteConfirmText"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch(names.DELETE_COMPETENCE, competence_id)
            .then(() => {
              toast(
                String(
                  this.$getVisibleNames(
                    "mesh.competence",
                    false,
                    "Competencia"
                  ) +
                    " " +
                    this.$t("reusable.common.deleted")
                )
              );
              this.tableReady = false;
              this.keyGenericTable++;
            });
        }
      });
    },
    slotCloseModal(competence_id) {
      this.$bvModal.hide(`edit-competence-modal-${competence_id}`);
      this.$bvModal.hide(`new-competence-modal`);
      // this.keyGenericTable++;
    },
  },
  mounted() {},
  created() {
    // Lookups globales pequeños que no dependen de las competencias visibles
    // solo quitar cuando se cambie el endpoint de PROFILE_COMPETENCES_MENTION
    this.$store.dispatch(names.FETCH_PROFILE_COMPETENCES_MENTION);
    this.$store.dispatch(names.FETCH_CAREERS);
    this.$store.dispatch(names.FETCH_CYCLES);
    this.$store.dispatch(names.FETCH_COMPETENCE_TYPES);
  },
};
</script>

<style scoped src="@/utils/rich_text_editor.css"></style>
<style scoped>
.badge-popover {
  overflow: auto;
  max-height: 350px;
}
.profiles {
  background-color: var(--secondary-color);
  border-radius: 4px;
}
.pointer-class {
  cursor: pointer;
}
.profiles-container {
  display: none;
}
.profiles-content {
  border-bottom: 0.1px solid var(--secondary-color);
  margin: 0 !important;
  margin-top: 0.4em !important;
  min-height: fit-content;
  min-width: fit-content;
}
.profiles-content:last-child {
  border-bottom: none;
}
.ra-competences-container {
  max-height: 500px;
  overflow-y: auto;
}
.container-badge-profile {
  justify-content: center;
}
@media print {
  .badge {
    border: none;
  }
  .profiles-container {
    display: flex;
    flex-direction: column;
  }
  .badge-profile {
    display: none;
  }
}
</style>
